/**
 * @voltxampere/ui — MergeNetsCommand (Sprint 14, ADR-0021)
 *
 * Two distinct nets get fused into one when a wire bridges them.
 * Sprint 13's WireTool warned and silently kept the first
 * netId (D3); Sprint 14 closes that gap by emitting this
 * command so the merge participates in undo / redo.
 *
 * Priority for which netId survives:
 *   1. Ground wins — the net carrying `isGround: true` is the
 *      anchor of the whole circuit; it never disappears.
 *   2. Otherwise the lexicographically smaller netId wins. The
 *      tiebreak is deterministic so replays land on the same
 *      result regardless of user click order.
 *
 * Both nets being ground is impossible: `validateCircuitInvariants`
 * already enforces "exactly one ground per circuit" at the
 * schema layer.
 *
 * Undo restores the loser net plus every wire / terminal that
 * referenced it before the merge — a snapshot is captured in the
 * constructor so we don't have to walk the post-merge circuit
 * for clues.
 *
 * Copyright © 2026 360 Studio
 * Licensed under MIT (see LICENSE file in repo root)
 */
export class MergeNetsCommand {
    winnerNetId;
    loserNetId;
    loserSnapshot;
    affectedTerminals;
    affectedWires;
    constructor(netA, netB, circuit) {
        if (netA === netB) {
            throw new Error("MergeNetsCommand requires two distinct net ids");
        }
        const a = circuit.nets.find((n) => n.id === netA);
        const b = circuit.nets.find((n) => n.id === netB);
        if (!a || !b) {
            throw new Error(`MergeNetsCommand: net not found (${netA}, ${netB})`);
        }
        if (a.isGround) {
            this.winnerNetId = a.id;
            this.loserNetId = b.id;
            this.loserSnapshot = b;
        }
        else if (b.isGround) {
            this.winnerNetId = b.id;
            this.loserNetId = a.id;
            this.loserSnapshot = a;
        }
        else if (a.id < b.id) {
            this.winnerNetId = a.id;
            this.loserNetId = b.id;
            this.loserSnapshot = b;
        }
        else {
            this.winnerNetId = b.id;
            this.loserNetId = a.id;
            this.loserSnapshot = a;
        }
        const terminals = [];
        for (const cmp of circuit.components) {
            cmp.terminals.forEach((t, idx) => {
                if (t.netId === this.loserNetId) {
                    terminals.push({ componentId: cmp.id, terminalIndex: idx });
                }
            });
        }
        this.affectedTerminals = terminals;
        this.affectedWires = circuit.wires
            .filter((w) => w.netId === this.loserNetId)
            .map((w) => ({ wireId: w.id }));
    }
    do(circuit) {
        return {
            ...circuit,
            nets: circuit.nets.filter((n) => n.id !== this.loserNetId),
            components: circuit.components.map((c) => ({
                ...c,
                terminals: c.terminals.map((t) => t.netId === this.loserNetId ? { ...t, netId: this.winnerNetId } : t),
            })),
            wires: circuit.wires.map((w) => w.netId === this.loserNetId ? { ...w, netId: this.winnerNetId } : w),
        };
    }
    undo(circuit) {
        const terminalKeys = new Set(this.affectedTerminals.map((t) => `${t.componentId}#${String(t.terminalIndex)}`));
        const wireIds = new Set(this.affectedWires.map((w) => w.wireId));
        return {
            ...circuit,
            nets: circuit.nets.some((n) => n.id === this.loserNetId)
                ? circuit.nets
                : [...circuit.nets, this.loserSnapshot],
            components: circuit.components.map((c) => ({
                ...c,
                terminals: c.terminals.map((t, idx) => terminalKeys.has(`${c.id}#${String(idx)}`) ? { ...t, netId: this.loserNetId } : t),
            })),
            wires: circuit.wires.map((w) => (wireIds.has(w.id) ? { ...w, netId: this.loserNetId } : w)),
        };
    }
    describe() {
        return `Net birleştirildi: ${this.loserNetId} → ${this.winnerNetId}`;
    }
}
//# sourceMappingURL=merge-nets.js.map